import React, { createContext, useMemo, useContext, useState, useRef, useEffect, createElement, forwardRef } from 'react';
import { Ninetailed, selectVariant, selectHasExperienceVariants } from '@ninetailed/experience.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { logger, isBrowser } from '@ninetailed/experience.js-shared';
import { isEqual, get } from 'radash';
import { isForwardRef } from 'react-is';
const NinetailedContext = /*#__PURE__*/createContext(undefined);
const NinetailedProvider = props => {
  const ninetailed = useMemo(() => {
    if ('ninetailed' in props) {
      return props.ninetailed;
    }
    const {
      clientId,
      environment,
      preview,
      url,
      locale,
      requestTimeout,
      plugins = [],
      onLog,
      onError,
      buildClientContext,
      onInitProfileId,
      componentViewTrackingThreshold,
      storageImpl,
      useSDKEvaluation
    } = props;
    return new Ninetailed({
      clientId,
      environment,
      preview
    }, {
      url,
      plugins,
      locale,
      requestTimeout,
      onLog,
      onError,
      buildClientContext,
      onInitProfileId,
      componentViewTrackingThreshold,
      storageImpl,
      useSDKEvaluation
    });
  }, []);
  const {
    children
  } = props;
  return /*#__PURE__*/jsx(NinetailedContext.Provider, {
    value: ninetailed,
    children: children
  });
};
const useNinetailed = () => {
  const ninetailed = useContext(NinetailedContext);
  if (ninetailed === undefined) {
    throw new Error('The component using the the context must be a descendant of the NinetailedProvider');
  }
  return ninetailed;
};
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
const _excluded$3 = ["experiences"];
const useProfile = () => {
  const ninetailed = useNinetailed();
  const [profileState, setProfileState] = useState(ninetailed.profileState);
  const profileStateRef = useRef({});

  /**
   * This effect compares the old and new profile state before updating it.
   * We use a ref to avoid an infinite loop which can happen when an empty profile state was updated with no changes.
   * This behaviour occurred as the validation handling on the error property was not set properly in the "CreateProfile" and "UpdateProfile" endpoint types.
   * Furthermore, it was also observed, that it "only" occurred when the preview plugin was used in parallel.
   */
  useEffect(() => {
    ninetailed.onProfileChange(profileState => {
      if (isEqual(profileState, profileStateRef.current)) {
        logger.debug('Profile State Did Not Change', profileState);
        return;
      } else {
        setProfileState(profileState);
        profileStateRef.current = profileState;
        logger.debug('Profile State Changed', profileState);
      }
    });
  }, []);
  const profileStateWithoutExperiences = _objectWithoutPropertiesLoose(profileState, _excluded$3);
  return Object.assign({}, profileStateWithoutExperiences, {
    loading: profileState.status === 'loading'
  });
};
const usePersonalize = function (baseline, variants) {
  let options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {
    holdout: -1
  };
  const profile = useProfile();
  return selectVariant(baseline, variants, profile, options);
};
const TrackHasSeenComponent = _ref3 => {
  let {
    children,
    variant,
    audience,
    isPersonalized
  } = _ref3;
  const ninetailed = useNinetailed();
  const [inView, setInView] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(_ref4 => {
      let [entry] = _ref4;
      if (entry.isIntersecting) {
        setInView(true);

        // Disconnect the observer since we only want to track
        // the first time the component is seen
        observer.disconnect();
      }
    });
    if (ref.current) {
      observer.observe(ref.current);
    }
    return () => {
      observer.disconnect();
    };
  }, []);
  useEffect(() => {
    if (isBrowser() && inView) {
      ninetailed.trackHasSeenComponent({
        variant,
        audience,
        isPersonalized
      });
    }
  }, [inView]);
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx("div", {
      ref: ref
    }), children]
  });
};
const _excluded$2 = ["component", "loadingComponent", "variants", "holdout"];
const Personalize = _ref => {
  let {
      component: Component,
      loadingComponent: LoadingComponent,
      variants = [],
      holdout = -1
    } = _ref,
    baseline = _objectWithoutPropertiesLoose(_ref, _excluded$2);
  const {
    loading,
    variant,
    isPersonalized,
    audience
  } = usePersonalize(baseline, variants, {
    holdout
  });
  const hasVariants = variants.length > 0;
  if (!hasVariants) {
    return /*#__PURE__*/jsx(Component, Object.assign({}, baseline, {
      ninetailed: {
        isPersonalized,
        audience
      }
    }));
  }
  if (loading) {
    if (LoadingComponent) {
      return /*#__PURE__*/jsx(LoadingComponent, {});
    }
    return /*#__PURE__*/jsx("div", {
      style: {
        opacity: 0
      },
      children: /*#__PURE__*/jsx(Component, Object.assign({}, variant, {
        ninetailed: {
          isPersonalized,
          audience
        }
      }))
    }, "hide");
  }
  return /*#__PURE__*/jsx(TrackHasSeenComponent, {
    variant: variant,
    audience: audience,
    isPersonalized: isPersonalized,
    children: /*#__PURE__*/createElement(Component, Object.assign({}, variant, {
      key: `${audience.id}-${variant.id}`,
      ninetailed: {
        isPersonalized,
        audience
      }
    }))
  });
};
const generateSelectors = id => {
  return id.split('_').map((path, index, paths) => {
    const dotPath = paths.slice(0, index).join('.');
    const underScorePath = paths.slice(index).join('_');
    return [dotPath, underScorePath].filter(path => path !== '').join('.');
  });
};
const selectValueFromProfile = (profile, id) => {
  const selectors = generateSelectors(id);
  const matchingSelector = selectors.find(selector => get(profile, selector));
  if (!matchingSelector) {
    return null;
  }
  return get(profile, matchingSelector);
};
const MergeTag = _ref5 => {
  let {
    id,
    fallback
  } = _ref5;
  const {
    loading,
    profile
  } = useProfile();
  if (loading || !profile) {
    return null;
  }
  const value = selectValueFromProfile(profile, id) || fallback;
  return value ? /*#__PURE__*/jsx(Fragment, {
    children: value
  }) : null;
};
const useExperience = _ref6 => {
  let {
    baseline,
    experiences
  } = _ref6;
  const ninetailed = useNinetailed();
  const hasVariants = experiences.map(experience => selectHasExperienceVariants(experience, baseline)).reduce((acc, curr) => acc || curr, false);
  const [experience, setExperience] = useState({
    hasVariants,
    baseline,
    error: null,
    loading: true,
    status: 'loading',
    experience: null,
    variant: baseline,
    variantIndex: 0,
    audience: null,
    isPersonalized: false,
    profile: null
  });
  useEffect(() => {
    return ninetailed.onSelectVariant({
      baseline,
      experiences
    }, state => {
      setExperience(state);
    });
  }, [JSON.stringify(baseline), JSON.stringify(experiences)]);
  return experience;
};
const ComponentMarker = /*#__PURE__*/forwardRef((_, ref) => {
  const {
    logger
  } = useNinetailed();
  const markerRef = useRef(null);
  useEffect(() => {
    var _markerRef$current;
    /*
    Due to React's limitation on setting !important styles during rendering, we set the display property on the DOM element directly.
    See: https://github.com/facebook/react/issues/1881
    */
    (_markerRef$current = markerRef.current) == null || _markerRef$current.style.setProperty('display', 'none', 'important');
  }, []);
  useEffect(() => {
    logger.debug('Using fallback mechanism to detect when experiences are seen. This can lead to inaccurate results. Consider using a forwardRef instead. See: https://docs.ninetailed.io/for-developers/experience-sdk/rendering-experiences#tracking-impressions-of-experiences');
  }, [logger]);
  useEffect(() => {
    if (markerRef.current) {
      const observableElement = getObservableElement(markerRef.current);
      if (ref) {
        if (typeof ref === 'function') {
          ref(observableElement);
        } else {
          ref.current = observableElement;
        }
      }
    }
  }, []);
  return /*#__PURE__*/jsx("div", {
    className: "nt-cmp-marker",
    style: {
      display: 'none'
    },
    ref: markerRef
  });
});
const getObservableSibling = element => {
  const nextSibling = element.nextElementSibling;
  if (!nextSibling) {
    return null;
  }
  const nextSiblingStyle = getComputedStyle(nextSibling);

  // Elements with display: none are not observable by the IntersectionObserver
  if (nextSiblingStyle.display !== 'none') {
    return nextSibling;
  }
  return getObservableSibling(nextSibling);
};
const getObservableElement = element => {
  const observableSibling = getObservableSibling(element);
  if (observableSibling) {
    return observableSibling;
  }
  const {
    parentElement
  } = element;
  if (!parentElement) {
    return null;
  }
  return getObservableElement(parentElement);
};
const _excluded$1 = ["component", "unhideAfterMs", "passthroughProps"],
  _excluded2 = ["experiences", "component", "loadingComponent", "passthroughProps"];
const DefaultExperienceLoadingComponent = _ref => {
  let {
      component: Component,
      unhideAfterMs = 5000,
      passthroughProps
    } = _ref,
    baseline = _objectWithoutPropertiesLoose(_ref, _excluded$1);
  const {
    logger
  } = useNinetailed();
  const [hidden, setHidden] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => {
      setHidden(false);
      logger.error(new Error(`The experience was still in loading state after ${unhideAfterMs}ms. That happens when no events are sent to the Ninetailed API. The baseline is now shown instead.`));
    }, unhideAfterMs);
    return () => {
      clearTimeout(timer);
    };
  }, []);
  if (hidden) {
    return /*#__PURE__*/jsx("div", {
      style: {
        opacity: 0
      },
      children: /*#__PURE__*/jsx(Component, Object.assign({}, passthroughProps, baseline, {
        ninetailed: {
          isPersonalized: false,
          audience: {
            id: 'baseline'
          }
        }
      }))
    }, "hide");
  }
  return /*#__PURE__*/jsx(Component, Object.assign({}, passthroughProps, baseline, {
    ninetailed: {
      isPersonalized: false,
      audience: {
        id: 'baseline'
      }
    }
  }));
};
const Experience = _ref2 => {
  let {
      experiences,
      component: Component,
      loadingComponent: LoadingComponent = DefaultExperienceLoadingComponent,
      passthroughProps
    } = _ref2,
    baseline = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const {
    observeElement,
    unobserveElement,
    logger
  } = useNinetailed();
  const {
    status,
    hasVariants,
    experience,
    variant,
    variantIndex,
    audience,
    isPersonalized
  } = useExperience({
    baseline,
    experiences
  });

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const isComponentForwardRef = isForwardRef(/*#__PURE__*/jsx(Component, {}));
  const componentRef = useRef(null);
  const isVariantHidden = 'hidden' in variant && variant.hidden;
  useEffect(() => {
    const componentElement = componentRef.current;
    if (componentElement && !(componentElement instanceof Element)) {
      const isObject = typeof componentElement === 'object' && componentElement !== null;
      const constructorName = isObject ? componentElement.constructor.name : '';
      const isConstructorNameNotObject = constructorName && constructorName !== 'Object';
      logger.warn(`The component ref being in Experience is an invalid element. Expected an Element but got ${typeof componentElement}${isConstructorNameNotObject ? ` of type ${constructorName}` : ''}. This component won't be observed.`);
      return () => {
        // noop
      };
    }
    if (componentElement) {
      observeElement({
        element: componentElement,
        experience,
        audience,
        variant: isVariantHidden ? Object.assign({}, variant, {
          id: `${baseline.id}-hidden`
        }) : variant,
        variantIndex
      });
      return () => {
        if (componentElement) {
          unobserveElement(componentElement);
        }
      };
    }
    return () => {
      // noop
    };
  }, [observeElement, unobserveElement, experience, baseline, variant, variantIndex, audience, isVariantHidden]);
  if (!hasVariants) {
    return /*#__PURE__*/jsxs(Fragment, {
      children: [!isComponentForwardRef && /*#__PURE__*/jsx(ComponentMarker, {
        ref: componentRef
      }, `marker-no-variants-${(experience == null ? void 0 : experience.id) || 'baseline'}-${variant.id}`), /*#__PURE__*/createElement(Component, Object.assign({}, passthroughProps, baseline, {
        key: baseline.id
      }, isComponentForwardRef ? {
        ref: componentRef
      } : {}))]
    });
  }
  if (status === 'loading') {
    return /*#__PURE__*/createElement(LoadingComponent, Object.assign({}, baseline, {
      key: baseline.id,
      passthroughProps: passthroughProps,
      experiences: experiences,
      component: Component
    }));
  }
  if (isVariantHidden) {
    return /*#__PURE__*/jsx(ComponentMarker, {
      ref: componentRef
    }, `marker-hidden-${(experience == null ? void 0 : experience.id) || 'baseline'}-${variant.id}`);
  }
  return /*#__PURE__*/jsxs(Fragment, {
    children: [!isComponentForwardRef && /*#__PURE__*/jsx(ComponentMarker, {
      ref: componentRef
    }, `marker-${(experience == null ? void 0 : experience.id) || 'baseline'}-${variant.id}`), /*#__PURE__*/createElement(Component, Object.assign({}, Object.assign({}, passthroughProps, variant), {
      key: `${(experience == null ? void 0 : experience.id) || 'baseline'}-${variant.id}`,
      ninetailed: {
        isPersonalized,
        audience: {
          id: (audience == null ? void 0 : audience.id) || 'all visitors'
        }
      }
    }, isComponentForwardRef ? {
      ref: componentRef
    } : {}))]
  });
};
const _excluded = ["experiences", "component", "passthroughProps"];
const ESRContext = /*#__PURE__*/React.createContext(undefined);
const ESRProvider = _ref7 => {
  let {
    experienceVariantsMap,
    children
  } = _ref7;
  return /*#__PURE__*/jsx(ESRContext.Provider, {
    value: {
      experienceVariantsMap
    },
    children: children
  });
};
const useESR = () => {
  const context = React.useContext(ESRContext);
  if (context === undefined) {
    throw new Error('The component using the the context must be a descendant of the ESRProvider');
  }
  return {
    experienceVariantsMap: context.experienceVariantsMap
  };
};
const ESRLoadingComponent = _ref => {
  let {
      experiences,
      component: Component,
      passthroughProps
    } = _ref,
    baseline = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    experienceVariantsMap
  } = useESR();
  const experience = experiences.find(experience => Object.prototype.hasOwnProperty.call(experienceVariantsMap, experience.id));
  if (!experience) {
    return /*#__PURE__*/jsx(Component, Object.assign({}, passthroughProps, baseline, {
      ninetailed: {
        isPersonalized: false,
        audience: {
          id: 'baseline'
        }
      }
    }));
  }
  const component = experience.components.find(component => component.baseline.id === baseline.id);
  if (!component) {
    return /*#__PURE__*/jsx(Component, Object.assign({}, passthroughProps, baseline, {
      ninetailed: {
        isPersonalized: false,
        audience: {
          id: 'baseline'
        }
      }
    }));
  }
  if (experienceVariantsMap[experience.id] === 0) {
    return /*#__PURE__*/jsx(Component, Object.assign({}, passthroughProps, baseline, {
      ninetailed: {
        isPersonalized: false,
        audience: {
          id: 'baseline'
        }
      }
    }));
  }
  const variant = component.variants[experienceVariantsMap[experience.id] - 1];
  if (!variant) {
    return /*#__PURE__*/jsx(Component, Object.assign({}, passthroughProps, baseline, {
      ninetailed: {
        isPersonalized: false,
        audience: {
          id: 'baseline'
        }
      }
    }));
  }
  return /*#__PURE__*/jsx(Component, Object.assign({}, passthroughProps, variant, {
    ninetailed: {
      isPersonalized: false,
      audience: {
        id: 'baseline'
      }
    }
  }));
};
export { DefaultExperienceLoadingComponent, ESRLoadingComponent, ESRProvider, Experience, MergeTag, NinetailedProvider, Personalize, TrackHasSeenComponent, useExperience, useNinetailed, usePersonalize, useProfile };